/**
 * i18n 키 소유권 검사 (G9) — `<moduleKey>.<kind>.<rest>`, 예외 0개.
 *
 * ─── 왜 module-first 인가 ───
 * 1번 세그먼트가 **소유자**다. 그래야 로케일을 모듈별로 쪼갤 수 있고, 모듈을 해지하면
 * 그 접두사를 통째로 들어낼 수 있고, 이 검사가 "이 키의 주인이 이 모듈인가"를 접두사 하나로
 * 판정할 수 있다. kind-first(`perm.core.…`)는 셋 다 안 된다 — `perm.` 아래에 전 모듈의 키가
 * 섞이기 때문이다. 구 stationon-erp 의 **6,112줄 단일 로케일**이 정확히 그 상태였다.
 *
 * ─── 왜 소스 스캔이 아니라 객체 순회인가 ───
 * `apps/server/src/composition.ts` 가 vertical 의 모듈 라벨을 `` `${m.key}.module.label` `` 로
 * **코드로 만든다.** 소스에서 문자열을 긁는 검사는 그 키를 영원히 못 본다.
 * 실측(2026-09-02): 개명 전 그 자리가 `` `module.${m.key}.label` ``(kind-first)이었고,
 * 같은 vertical 이 자기 매니페스트에서는 `stationon.group.shooting`(module-first)을 썼다 —
 * **한 모듈 안에서 두 규약이 갈라져 있었다.** 조립된 객체를 걸어야만 보이는 형태다.
 *
 * ─── 예외를 두지 않는 이유 ───
 * `error.*` 18개를 예외로 남기자는 선택지가 있었다. 예외를 하나 만들면 이 검사는 예외 목록을
 * 갖게 되고, 그 목록이 자라는 것이 구 시스템의 `permissionAliases.ts`(백엔드·프론트 수작업
 * 미러링)다. 에러 레지스트리는 `packages/contracts` 에 있으므로 주인은 `core` 이고,
 * 키는 `core.error.…` 가 된다. **`(status, code)` 계약은 한 글자도 안 바뀐다** —
 * 바뀌는 것은 `messageKey`·`nextActionKey` 문자열뿐이다. (docs/decisions/0008)
 *
 * ─── 아직 안 하는 것: 존재 검사 ───
 * "선언된 키가 로케일 카탈로그에 실제로 있는가"는 로케일 파일이 생기는 §구현 순서 10에서
 * 붙인다. 지금 켜면 검사 대상이 0개다 — `audit.ts` 가 "검사 대상이 0개인 게이트는 통과가
 * 아니라 무력화"라고 막은 바로 그 상태를 이 파일이 만들게 된다.
 */
import type { RbacManifest } from './rbac.js';

/**
 * 2번 세그먼트로 올 수 있는 것. **닫힌 집합이다.**
 *
 * 열어 두면 `core.label`·`core.text.…` 같은 것이 슬금슬금 들어오고 그 순간 2번 자리가
 * 아무 뜻도 없어진다. 새 kind 가 필요하면 이 배열을 늘리고, 그 한 줄이 PR diff 에 뜬다
 * (`PERMISSION_ACTIONS` 와 같은 수법).
 */
export const I18N_KINDS = [
  'module', 'group', 'perm', 'role', 'denied', 'error', 'screen',
  /*
   * `payroll` — 급여 명세서의 항목 이름. **2026-09-03 에 늘렸고, 이 줄이 그 diff 다.**
   *
   * 두 번째 vertical 을 붙이면서 드러났다: 급여 컴포넌트가 내는 `labelKey` 는 사람이 읽는
   * 문장인데(명세서 PDF 가 렌더한다) 이 집합에 자리가 없어서 **모듈 사전에 적을 수가
   * 없었다** — `assertMessages` 가 거절한다. 그래서 `stationon.payroll.shootingPieceRate`
   * 는 문장 없이 살아 있었고, 사용처 스캔은 그 키를 볼 수 없어서(객체 리터럴의 필드다)
   * 어떤 게이트도 결핍을 못 잡았다. "규약이 표현할 수 없는 키"와 "그 결핍을 볼 수 없는
   * 게이트"가 겹쳐 있던 자리다.
   *
   * 키는 **선언하지 않고 파생한다** — `payrollLabelKeyOf()`(extensions/payroll.ts)가
   * provider 키에서 만든다. 선언 필드를 새로 두면 그것이 곧 두 번째 원본이고,
   * compute() 가 내는 키와 갈라져도 아무도 모른다.
   */
  'payroll',
  /*
   * `cred` — 자격증명 칸의 이름. **2026-09-14 에 늘렸다(0060).**
   *
   * `CredentialSpec[]` 에서 admin 폼이 자동 생성되므로 그 라벨은 사람이 읽는 문장이고,
   * 이 집합에 자리가 없으면 `assertMessages` 가 사전 등록을 거절한다 — `payroll` kind 가
   * 생긴 것과 **같은 사고의 두 번째 얼굴**이다(규약이 표현할 수 없는 키).
   * 이번에는 결핍이 조용하지 않았다: `register()` 의 `assertCredentials` 가
   * labelKey 접두사를 요구하고, 아래 `collectModuleI18nKeys` 가 그 키를 수집한다.
   */
  'cred',
  /*
   * `job` — 자동 작업의 이름과 결과 문장. **2026-09-04 에 늘렸고, 이 줄이 그 diff 다.**
   *
   * 잡은 모듈이 소유하고(키의 1번 자리가 그 모듈이다) 사람이 읽는 문장을 둘 낸다:
   * 목록의 이름과 "53건 부여 · 10명 입사일 없음" 같은 결과 한 줄. 둘 다 **선언하지 않고
   * 파생한다**(`jobLabelKeyOf`·`jobResultKeyOf`) — 선언 필드를 두면 그것이 두 번째 원본이고,
   * 실행 원장에 남은 파라미터와 짝이 안 맞아도 아무도 모른다.
   * `screen` 으로 대신하지 않은 이유: 잡은 화면이 아니다. 화면 없이 도는 잡도 있고
   * (`manual: { none }`), 그때 `screen.` 접두사는 존재하지 않는 화면을 가리키게 된다.
   */
  'job',
  /*
   * `notice` — **사내 알림의 문장.** 2026-09-04 에 늘렸고, 이 줄이 그 diff 다.
   *
   * 알림은 모듈이 만들고(휴가 결재는 `time`) 사람이 읽는 한 문장을 낸다. 그 문장을
   * 행에 저장하지 않는 이유가 이 kind 가 필요한 이유다: 저장하면 로케일이 둘이 되는 날
   * 지난 알림만 번역되지 않는다. 그래서 행에는 `(kind, params)` 만 남고 문장은 사전에 있다.
   * 키는 **선언하지 않고 파생한다**(`noticeKeyOf`) — 잡의 `job` 과 같은 수법, 같은 이유.
   */
  'notice',
  /*
   * `export` — **내보낸 파일 안의 문장.** 2026-09-09 에 늘렸고, 이 줄이 그 diff 다. (0043)
   *
   * 엑셀의 열 제목 · 시트 이름 · PDF 문서의 항목 이름과 산식 한 줄이 여기 산다.
   * `screen` 으로 대신하지 않은 이유: **파일은 화면이 아니다.** 같은 문장이 화면에도
   * 있으면 그건 화면 키를 그대로 쓰면 되고(대장 엑셀의 '실지급액'이 실제로 그렇다),
   * 여기 오는 것은 **파일에만 있는 말**이다 — 시트 이름, 이체 파일의 '계좌 미등록',
   * 명세서 아래의 공제 산식.
   *
   * ★ 그리고 이 kind 에는 다른 것에 없는 성질이 하나 있다: **가져오기가 이 문장으로
   *   열을 찾는다.** 내보내기가 쓴 머리글을 사람이 고쳐서 다시 넣으므로, 여기를 고치면
   *   예전에 내보낸 파일이 안 들어간다. 사전이 규약의 일부인 유일한 자리다.
   */
  'export',
  /*
   * `nav` — **내비 구획의 이름.** 2026-09-12 에 늘렸고, 이 줄이 그 diff 다. (0057)
   *
   * 구획은 화면이 아니다 — `screen` 으로 대신하면 `core.screen.my` 가 존재하지 않는
   * 화면 하나를 가리키게 되고, 화면 키 양방향 대조(검사 1·2)가 그것을 찾다가
   * 못 찾는다. `group` 도 아니다: 그 kind 는 **역할 편집표의 권한 묶음**이고
   * (`core.group.people` = 권한 '인사' 묶음), 구획은 그것과 집합이 다르다 —
   * 권한 묶음 '자동 작업'은 구획 '설정'에 산다. 같은 kind 에 두면 두 어휘가
   * 한 이름 공간에서 서로를 덮는다.
   *
   * 키는 **선언한다**(`NAV_SECTIONS`). 잡·알림과 달리 파생이 아닌 이유는 구획이
   * 코드에 적힌 닫힌 목록이기 때문이다 — 그 목록이 곧 선언이다.
   */
  'nav',
] as const;
export type I18nKind = (typeof I18N_KINDS)[number];

/** 3번 세그먼트 이후. 점으로 나뉜 한 조각 이상. */
const REST_RE = /^[A-Za-z][A-Za-z0-9]*(-[A-Za-z0-9]+)*(\.[A-Za-z][A-Za-z0-9]*(-[A-Za-z0-9]+)*)*$/;

/** 검사 대상 하나 — 키와 "어디서 왔는지". 위치가 없으면 실패 메시지가 쓸모없어진다. */
export interface I18nKeyUse {
  key: string;
  /** 이 키를 선언한 모듈 키. 1번 세그먼트가 이것과 같아야 한다. */
  owner: string;
  /** 이 자리에 와야 하는 kind. 슬롯마다 정해져 있다 — 복붙 사고를 잡는다. */
  kind: I18nKind;
  /** 실패 메시지에 찍을 자리. 예: `core.permissions[core.rbac.role.read].deniedMessageKey` */
  where: string;
}

export interface I18nProblem {
  key: string;
  where: string;
  reason: string;
}

/** RbacManifest + 모듈 라벨에서 i18n 키를 전부 모은다. 새 슬롯이 생기면 여기에 추가한다. */
export function collectI18nKeys(
  modules: readonly {
    key: string; labelKey: string; rbac: RbacManifest;
    /** 자격증명 라벨(0060). 선택인 이유는 제품 모듈 투영에 이 필드가 없기 때문이다. */
    credentials?: readonly { key: string; labelKey: string }[];
  }[],
): I18nKeyUse[] {
  const out: I18nKeyUse[] = [];
  for (const m of modules) {
    out.push({ key: m.labelKey, owner: m.key, kind: 'module', where: `${m.key}.labelKey` });
    for (const g of m.rbac.groups)
      out.push({ key: g.labelKey, owner: m.key, kind: 'group', where: `${m.key}.groups[${g.key}].labelKey` });
    for (const p of m.rbac.permissions) {
      out.push({ key: p.labelKey, owner: m.key, kind: 'perm', where: `${m.key}.permissions[${p.key}].labelKey` });
      out.push({ key: p.deniedMessageKey, owner: m.key, kind: 'denied', where: `${m.key}.permissions[${p.key}].deniedMessageKey` });
    }
    for (const r of m.rbac.roleTemplates) {
      out.push({ key: r.labelKey, owner: m.key, kind: 'role', where: `${m.key}.roleTemplates[${r.key}].labelKey` });
      if (r.descriptionKey)
        out.push({ key: r.descriptionKey, owner: m.key, kind: 'role', where: `${m.key}.roleTemplates[${r.key}].descriptionKey` });
    }
    for (const j of m.rbac.jobTitles ?? [])
      out.push({ key: j.labelKey, owner: m.key, kind: 'role', where: `${m.key}.jobTitles[${j.key}].labelKey` });
    /* 자격증명 폼의 칸 이름(0060). 선언에서 폼이 생기므로 이 문장이 없으면 화면에 키가 뜬다. */
    for (const c of m.credentials ?? [])
      out.push({ key: c.labelKey, owner: m.key, kind: 'cred', where: `${m.key}.credentials[${c.key}].labelKey` });
  }
  return out;
}

/**
 * 에러 레지스트리의 키. 주인은 `core` 다 — 레지스트리가 코어 계약이기 때문이다.
 * `ERRORS` 를 여기서 import 하지 않고 인자로 받는다: 순환 import 를 만들지 않고,
 * 테스트가 작은 표로 이 함수를 부를 수 있다.
 */
export function collectErrorI18nKeys(
  errors: readonly { code: string; messageKey: string; nextActionKey?: string }[],
): I18nKeyUse[] {
  const out: I18nKeyUse[] = [];
  for (const e of errors) {
    out.push({ key: e.messageKey, owner: 'core', kind: 'error', where: `ERRORS[${e.code}].messageKey` });
    if (e.nextActionKey)
      out.push({ key: e.nextActionKey, owner: 'core', kind: 'error', where: `ERRORS[${e.code}].nextActionKey` });
  }
  return out;
}

/** 검사만 한다 — 던지지 않는다. 호출자가 전부 모아 한 번에 보고할 수 있게. */
export function checkI18nKeys(uses: readonly I18nKeyUse[]): I18nProblem[] {
  const problems: I18nProblem[] = [];
  // 같은 키가 뜻이 다른 두 자리에 쓰이면 로케일 한 줄이 두 곳을 렌더한다 — 한쪽이 반드시 어색해진다.
  const seen = new Map<string, I18nKeyUse>();

  for (const u of uses) {
    const [owner, kind, ...rest] = u.key.split('.');
    if (!owner || !kind || rest.length === 0) {
      problems.push({ key: u.key, where: u.where, reason: `세 조각 이상이어야 한다 — <moduleKey>.<kind>.<rest>` });
      continue;
    }
    if (owner !== u.owner) {
      problems.push({
        key: u.key, where: u.where,
        reason: `1번 세그먼트가 소유 모듈과 다르다 (기대: '${u.owner}', 실제: '${owner}'). ` +
          `kind-first 로 쓰지 않는다 — 1번 자리는 소유자다`,
      });
      continue;
    }
    if (kind !== u.kind) {
      problems.push({
        key: u.key, where: u.where,
        reason: `2번 세그먼트가 이 자리의 kind 와 다르다 (기대: '${u.kind}', 실제: '${kind}')`,
      });
      continue;
    }
    if (!REST_RE.test(rest.join('.'))) {
      problems.push({ key: u.key, where: u.where, reason: `3번 세그먼트 이후가 규약을 벗어난다: '${rest.join('.')}'` });
      continue;
    }
    const prev = seen.get(u.key);
    if (prev) problems.push({ key: u.key, where: u.where, reason: `같은 키가 '${prev.where}' 에서도 쓰인다 — 한 키는 한 뜻이다` });
    else seen.set(u.key, u);
  }
  return problems;
}

export class I18nKeyError extends Error {
  constructor(readonly problems: I18nProblem[]) {
    super(
      `i18n 키 검사 실패 (${problems.length}건):\n` +
        problems.map((p) => `  ✗ ${p.where}\n      '${p.key}' — ${p.reason}`).join('\n'),
    );
    this.name = 'I18nKeyError';
  }
}

/**
 * 게이트 진입점. 문제가 있으면 던지고, **검사 대상이 0개여도 던진다.**
 *
 * 0개를 통과시키면 조립이 깨져 키를 하나도 못 모은 날 이 게이트가 초록을 찍는다.
 * `audit.ts` 가 라우터 감사에서 막은 것과 같은 형태다 —
 * "검사 대상이 0개인 게이트는 통과가 아니라 무력화다."
 */
export function assertI18nKeys(uses: readonly I18nKeyUse[]): { checked: number } {
  const problems = checkI18nKeys(uses);
  if (problems.length) throw new I18nKeyError(problems);
  if (uses.length === 0) {
    throw new I18nKeyError([{
      key: '(없음)', where: '(입력 전체)',
      reason: 'i18n 키를 하나도 찾지 못했다. 빈 입력에 초록을 주지 않는다 — 검사 대상이 0개인 게이트는 통과가 아니라 무력화다',
    }]);
  }
  return { checked: uses.length };
}

/* ══════════════════════════════════════════════════════════════════════════
   사전 — 키가 문장이 되는 자리. (§구현 순서 11)

   위쪽 절반은 "키가 규약을 지키는가"를 본다. 아래 절반은 "그 키에 문장이 있는가"를 본다.
   둘을 한 파일에 둔 이유는 하나다: 규약과 사전이 갈라지면 **규약을 지키는 없는 키**가
   생기고, 그건 화면에 키가 그대로 뜨는 형태로만 드러난다.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * 로케일 사전. **키 → 렌더된 문장** 하나뿐이다.
 *
 * `Record<string, string>` 인 것은 게으름이 아니라 소유권 때문이다: 키의 규약은
 * 이 파일이 소유하고 `assertMessages` 가 판정한다. 타입으로 다시 좁히면 판정기가 둘이 된다.
 *
 * 이 타입이 web-sdk 가 아니라 여기 있는 이유(2026-09-02 에 옮겼다): 사전은
 * `session.bootstrap` 의 응답 필드가 되면서 **두 당사자의 계약**이 됐다. 서버가 만들고
 * 브라우저가 읽는 것을 브라우저 패키지가 소유할 수 없다.
 */
export type MessageDictionary = Readonly<Record<string, string>>;

/**
 * 지원 로케일. **오늘은 하나다.**
 *
 * 배열을 두는 이유는 확장을 미리 만드는 것이 아니라 반대다 — `'ko'` 를 여기저기 리터럴로
 * 적으면 두 번째 로케일이 오는 날 그 자리를 전부 찾아야 하고, 못 찾은 자리는 조용히
 * 한국어를 낸다. 여기에 `'en'` 을 더하는 순간 **모든 모듈이 컴파일에 실패한다**
 * (`ModuleLocales` 가 `Record<Locale, …>` 라 빠진 로케일이 타입 에러다) — 그게 의도다.
 */
export const LOCALES = ['ko'] as const;
export type Locale = (typeof LOCALES)[number];

/** 기본 로케일. 사용자별 선택이 생기기 전까지 부트가 이것을 실어 보낸다. */
export const DEFAULT_LOCALE: Locale = 'ko';

/**
 * 모듈이 내놓는 사전. **물음표가 없다** — `surface` 와 같은 이유다.
 * 선택 필드로 두면 빠뜨림이 기본값이 되고, 빠뜨린 모듈의 화면은 키가 그대로 뜬다.
 */
export type ModuleLocales = Readonly<Record<Locale, MessageDictionary>>;

/** 사전을 내놓는 것. 제품 모듈과 vertical 이 같은 모양이다. */
export interface MessageModule {
  readonly key: string;
  readonly locales: ModuleLocales;
}

/** 키를 세 조각으로 나눈다. 나뉘지 않으면 `null` — 호출자가 이유를 붙인다. */
function splitKey(key: string): { owner: string; kind: string; rest: string } | null {
  const [owner, kind, ...rest] = key.split('.');
  if (!owner || !kind || rest.length === 0) return null;
  return { owner, kind, rest: rest.join('.') };
}

/**
 * 사전의 키가 그 모듈 것인가. **선언 검사의 거울이다** —
 * 위쪽은 "선언한 키가 규약을 지키는가"를 보고, 이쪽은 "사전에 남의 키가 없는가"를 본다.
 *
 * 한쪽만 있으면 모듈 A 가 `b.screen.x` 문장을 정의할 수 있고, B 를 해지해도 그 문장이 남는다.
 * 접두사를 통째로 들어내는 것이 module-first 규약의 존재 이유이므로 그건 규약을 무효로 만든다.
 */
export function checkMessageOwnership(
  modules: readonly MessageModule[],
  locale: Locale,
): I18nProblem[] {
  const problems: I18nProblem[] = [];
  const kinds = new Set<string>(I18N_KINDS);
  for (const m of modules) {
    const dict = m.locales[locale];
    for (const key of Object.keys(dict)) {
      const where = `${m.key}.locales.${locale}[${key}]`;
      const parts = splitKey(key);
      if (!parts) {
        problems.push({ key, where, reason: '세 조각 이상이어야 한다 — <moduleKey>.<kind>.<rest>' });
        continue;
      }
      if (parts.owner !== m.key) {
        problems.push({
          key, where,
          reason: `이 사전은 '${m.key}' 의 것인데 키의 소유자가 '${parts.owner}' 다. ` +
            `남의 접두사를 정의하면 그 모듈을 해지해도 문장이 남는다`,
        });
        continue;
      }
      if (!kinds.has(parts.kind)) {
        problems.push({ key, where, reason: `2번 세그먼트가 닫힌 집합 밖이다: '${parts.kind}'` });
        continue;
      }
      if (!dict[key]!.trim()) {
        // 빈 문장은 "번역했다"로 보이면서 화면에서는 아무것도 아니다 — 키가 뜨는 것보다 나쁘다.
        problems.push({ key, where, reason: '문장이 비어 있다. 비워 둘 바에는 키를 지운다' });
      }
    }
  }
  return problems;
}

/**
 * 선언된 키에 문장이 있는가. **`i18n.ts` 가 "아직 안 하는 것"으로 남겨 둔 존재 검사다.**
 *
 * 그때 켜지 않은 이유는 사전이 0개라 검사 대상이 0개였기 때문이고, 사전이 생긴 지금이
 * 그 게이트가 대상을 갖는 첫 시점이다.
 */
export function checkMessagesExist(
  uses: readonly I18nKeyUse[],
  dict: MessageDictionary,
): I18nProblem[] {
  const problems: I18nProblem[] = [];
  for (const u of uses) {
    if (dict[u.key] === undefined) {
      problems.push({
        key: u.key, where: u.where,
        reason: `선언은 있는데 사전에 문장이 없다. 이 키는 화면에 **키 그대로** 뜬다 — ` +
          `폴백으로 그럴듯하게 만들지 않기로 했으므로(useMessage) 여기서 막는다`,
      });
    }
  }
  return problems;
}

/**
 * 모듈 사전을 하나로 합친다. **중복 키는 던진다.**
 *
 * 소유권 검사가 이미 접두사를 강제하므로 서로 다른 모듈의 키는 충돌할 수 없다 —
 * 여기서 잡히는 것은 같은 모듈이 사전을 두 번 등록한 경우(조립 사고)뿐이고,
 * 그 상태는 "나중 값이 이긴다"로 조용히 지나가면 안 된다.
 */
export function assembleMessages(
  modules: readonly MessageModule[],
  locale: Locale,
): MessageDictionary {
  const out: Record<string, string> = {};
  for (const m of modules) {
    for (const [key, text] of Object.entries(m.locales[locale])) {
      if (out[key] !== undefined) {
        throw new I18nKeyError([{
          key, where: `${m.key}.locales.${locale}`,
          reason: '같은 키가 두 번 조립됐다 — 모듈 목록에 같은 모듈이 두 번 들어 있다',
        }]);
      }
      out[key] = text;
    }
  }
  return out;
}

/**
 * 게이트 진입점. 소유권 + 존재를 한 번에 보고 **둘을 함께 보고한다** —
 * 하나씩 던지면 고치고 다시 돌리기를 반복하게 되고, 그 반복이 게이트를 끄는 이유가 된다.
 *
 * 사전이 비어 있으면 던진다. `assertI18nKeys` 와 같은 규칙이다:
 * 검사 대상이 0개인 게이트는 통과가 아니라 무력화다.
 */
export function assertMessages(input: {
  modules: readonly MessageModule[];
  locale: Locale;
  uses: readonly I18nKeyUse[];
}): { locale: Locale; keys: number } {
  const dict = assembleMessages(input.modules, input.locale);
  const problems = [
    ...checkMessageOwnership(input.modules, input.locale),
    ...checkMessagesExist(input.uses, dict),
  ];
  if (problems.length) throw new I18nKeyError(problems);
  const keys = Object.keys(dict).length;
  if (keys === 0) {
    throw new I18nKeyError([{
      key: '(없음)', where: `(로케일 ${input.locale} 전체)`,
      reason: '사전이 비어 있다. 빈 사전에 초록을 주지 않는다',
    }]);
  }
  return { locale: input.locale, keys };
}

/**
 * 문장 하나를 렌더한다. **서버와 웹이 같은 함수를 쓴다.**
 *
 * 두 곳에 적으면 갈라진다 — 실제로 갈라질 자리가 보인다: 웹은 `{name}` 자리를 채우고
 * 서버(`ctx.t`)는 모듈이 급여 컴포넌트 문장을 렌더할 때 같은 규칙을 필요로 한다.
 * 한쪽만 파라미터를 채우면 같은 급여가 화면에서는 "촬영 10건", PDF 에서는 "촬영 {image}건" 이 된다.
 *
 * **없는 키는 키 자체를 그린다.** 마지막 조각을 뽑아 그럴듯하게 만드는 폴백은
 * "번역이 빠졌다"를 "번역이 있다"로 위장한다 — 시끄러운 쪽이 이 리포의 선택이다.
 */
export function renderMessage(
  dict: MessageDictionary,
  key: string,
  params?: Readonly<Record<string, unknown>>,
): string {
  const raw = dict[key];
  if (raw === undefined) return key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.hasOwn(params, name) ? String(params[name]) : whole,
  );
}

/**
 * 보간이 **다 안 된** 문장인가. 즉 문장이 요구하는 파라미터를 준 쪽이 갖고 있지 않았다.
 *
 * ★ 왜 이 함수가 필요한가 (2026-09-05, 사용량 지표를 하나 늘리면서 드러났다)
 * 잡의 결과 문장은 사전에 있고 값은 원장(`job_runs.result_params`)에 있다 — 문장을 저장하지
 * 않는 것이 0022 의 규약이고, 그 이유는 로케일이 늘 때 지난 기록만 번역이 안 되기 때문이다.
 * 그런데 **파라미터의 모양도 버전이 있다.** 잡의 결과에 필드를 하나 더하는 순간, 그 전에
 * 적힌 행들은 새 문장이 요구하는 키를 안 갖고 있고 `translate` 는 못 채운 자리를
 * `{noJoinDate}` 처럼 **그대로 화면에 내보낸다.** 사용자에게 그건 고장이다.
 *
 * 고치는 자리를 `translate` 안이 아니라 여기로 뺀 이유: 빈 문자열로 바꾸면 "입사일 없음 명"
 * 이 되어 더 나쁘고, 무엇으로 대체할지는 **부르는 화면이 아는 것**이다.
 */
export function hasUnresolvedParams(rendered: string): boolean {
  return /\{\w+\}/.test(rendered);
}
